import type { RanchEvent } from '../sim/ranch/types';
import type { HudSnapshot } from '../sim/ranch/view';
import { el, formatMoney } from './dom';

const STAT_SHORT: Record<string, string> = {
  speed: 'SPD',
  stamina: 'STA',
  accel: 'ACC',
  heart: 'HRT',
};

const REJECT_MESSAGES: Record<string, string> = {
  'race-today': 'You have a race to run today!',
  'race-day-training': 'No training on race day.',
  'training-pending': 'Finish the current session first.',
  'training-not-mastered': 'Earn Gold in this exact drill before using Routine.',
  'no-training': 'No session in progress.',
  'no-energy': 'Too tired — out of energy for that.',
  'no-money': "Can't afford that.",
  'locked-league': 'Win 3 races in the league below first.',
  'bad-event': 'That race is not on the card.',
  'already-entered': 'Already entered.',
  'one-race-per-day': 'One race per horse each day.',
  'horse-entered': 'That horse already has a race that day.',
  'horse-raced-today': 'That horse has already raced today.',
  'horse-trained-today': 'A horse cannot train and race on the same day.',
  'too-far-ahead': 'Entries open one season out.',
  'not-entered': 'Not entered in that race.',
  'race-day-withdraw': "Can't scratch on race day — ride, or pull up at the gate.",
  'horse-injured': 'She needs stall rest first.',
  'no-vet': 'The vet clinic is not up to that yet.',
  'nothing-to-test': 'Nothing hidden to find.',
  'patch-used': 'One patch-up per season.',
  'no-injury': 'Nothing to patch.',
  'breeding-locked': 'Win the Grand Derby to unlock breeding.',
  'bad-mare': 'She is not ready to breed.',
  'bad-sire': 'That sire will not do.',
  'already-bred': 'Once a season per mare.',
  'too-young': 'Too young to race — give it a season.',
  'max-level': 'Fully upgraded.',
  'already-raced': "Today's race is already run.",
  'bad-name': 'That name will not do.',
  'no-future-entry': 'There is no future entry to advance to.',
};

const LEAGUE_NAMES = ['Local', 'Regional', 'National', 'Grand Derby'];

function ordinal(place: number): string {
  const suffix = place === 1 ? 'st' : place === 2 ? 'nd' : place === 3 ? 'rd' : 'th';
  return `${place}${suffix}`;
}

/** Persistent ranch chrome: top status bar, race-day banner, toasts. */
export class RanchHud {
  private readonly wrap: HTMLElement;
  private readonly dayText: HTMLElement;
  private readonly moneyText: HTMLElement;
  private readonly energyText: HTMLElement;
  private readonly horseText: HTMLElement;
  private readonly statsText: HTMLElement;
  private readonly banner: HTMLElement;
  private readonly toasts: HTMLElement;
  private lastKey = '';

  constructor(root: HTMLElement) {
    this.dayText = el('span', { class: 'hud-chip' });
    this.moneyText = el('span', { class: 'hud-chip money' });
    this.energyText = el('span', { class: 'hud-chip' });
    this.horseText = el('span', { class: 'hud-chip horse' });
    this.statsText = el('span', { class: 'hud-chip stats' });
    this.banner = el('div', { class: 'race-banner' });
    this.toasts = el('div', { class: 'toast-stack' });
    this.wrap = el(
      'div',
      { class: 'ranch-hud' },
      el(
        'div',
        { class: 'ranch-topbar' },
        this.dayText,
        this.moneyText,
        this.energyText,
        this.horseText,
        this.statsText,
      ),
      this.banner,
      this.toasts,
      el('div', { class: 'ranch-hints' }, '1-4 facilities · B barn · F feed · N races · E end day'),
    );
    root.append(this.wrap);
  }

  update(snapshot: HudSnapshot): void {
    const statBits = snapshot.hasHorse
      ? (['speed', 'stamina', 'accel', 'heart'] as const)
          .map((stat) => `${STAT_SHORT[stat]} ${snapshot.stats[stat].level}/${snapshot.stats[stat].cap}`)
          .join(' · ')
      : '';
    const banner = !snapshot.hasHorse
      ? 'No racer saddled — open the Barn (B) to promote or buy one!'
      : snapshot.injuryDays > 0
        ? `Injured — ${snapshot.injuryDays} day${snapshot.injuryDays === 1 ? '' : 's'} of stall rest.`
        : snapshot.raceToday
        ? snapshot.raceToday.done
          ? ''
          : `RACE DAY — ${LEAGUE_NAMES[snapshot.raceToday.league]} · open the board (N) to ride!`
        : snapshot.nextEntry
          ? `Next race: day ${snapshot.nextEntry.day} (${LEAGUE_NAMES[snapshot.nextEntry.league]})`
          : '';
    const fatigueLabel = !snapshot.hasHorse
      ? ''
      : snapshot.fatigueVisible
        ? ` · 😓${snapshot.fatigue}`
        : snapshot.fatigue >= 70
          ? ' · worn'
          : snapshot.fatigue >= 40
            ? ' · tired'
            : '';
    const horseLabel = snapshot.hasHorse
      ? `${snapshot.horseName} (${snapshot.horseAge}yo)${fatigueLabel}`
      : snapshot.horseName;
    const key = `${snapshot.day}|${snapshot.moneyCents}|${snapshot.energy}/${snapshot.energyMax}|${horseLabel}|${statBits}|${banner}`;
    if (key === this.lastKey) return;
    this.lastKey = key;

    this.dayText.textContent = `S${snapshot.season} · Day ${snapshot.seasonDay}/12`;
    this.moneyText.textContent = formatMoney(snapshot.moneyCents);
    this.energyText.textContent = `⚡ ${snapshot.energy}/${snapshot.energyMax} (${snapshot.feedPlanName})`;
    this.horseText.textContent = horseLabel;
    this.statsText.textContent = statBits;
    this.banner.textContent = banner;
    this.banner.classList.toggle(
      'urgent',
      !snapshot.hasHorse || Boolean(snapshot.raceToday && !snapshot.raceToday.done),
    );
  }

  handleEvents(events: RanchEvent[]): void {
    for (const event of events) {
      switch (event.kind) {
        case 'feedDowngraded':
          this.toast('Feed money ran dry — back to free pasture.', 'bad');
          break;
        case 'statLevel':
          this.toast(`${STAT_SHORT[event.stat]} up! Level ${event.level}`, 'good');
          break;
        case 'capBlocked':
          this.toast(`${STAT_SHORT[event.stat]} is at the facility cap — upgrade to train past it.`, 'warn');
          break;
        case 'trainingResolved':
          this.toast(`${event.routine ? 'Routine · ' : ''}+${event.xp} ${STAT_SHORT[event.stat]} XP`, 'good');
          break;
        case 'trainingMastered':
          this.toast('Gold work — Routine unlocked for that drill!', 'good');
          break;
        case 'trainingAborted':
          this.toast('Session scratched — energy back.', 'warn');
          break;
        case 'seasonRollover':
          this.toast(`Season ${event.season} begins — fresh yearlings at the sale.`, 'good');
          break;
        case 'lostAStep':
          this.toast('The years are showing — a step lost to age.', 'warn');
          break;
        case 'horseRetired':
          this.toast(event.forced ? 'Retired — age catches every horse.' : 'Retired to the barn.', 'warn');
          break;
        case 'horsePromoted':
          this.toast('Saddled up!', 'good');
          break;
        case 'horseSold':
          this.toast(`Sold — ${formatMoney(event.priceCents)}.`, 'good');
          break;
        case 'horseBought':
          this.toast(`${event.name} joins the string!`, 'good');
          break;
        case 'stableUpgraded':
          this.toast('More stalls built.', 'good');
          break;
        case 'derbyWon':
          this.toast('GRAND DERBY CHAMPION! The bloodline is made.', 'good');
          break;
        case 'bred':
          this.toast(event.feeCents > 0 ? `Covered — ${formatMoney(event.feeCents)} stud fee.` : 'Covered.', 'good');
          break;
        case 'foalBorn':
          this.toast(`A foal! ${event.name} arrives in the barn.`, 'good');
          break;
        case 'traitRevealed':
          this.toast('A trait shows itself — check the barn sheet.', 'good');
          break;
        case 'horseInjured':
          this.toast(`Pulled up sore — ${event.days} days of rest.`, 'bad');
          break;
        case 'horseHealed':
          this.toast('Sound again and ready to work.', 'good');
          break;
        case 'vetUpgraded':
          this.toast('The vet clinic grows.', 'good');
          break;
        case 'bloodlineTested':
          this.toast(event.trait ? 'The bloodline test found something!' : 'Bloodline test: nothing hidden.', event.trait ? 'good' : 'warn');
          break;
        case 'patchedUp':
          this.toast('Patched up — layoff halved.', 'good');
          break;
        case 'raceEntered':
          this.toast('Entered the race.', 'good');
          break;
        case 'raceWithdrawn':
          this.toast('Scratched. No refund.', 'warn');
          break;
        case 'raceScratched':
          this.toast('An unavailable horse was scratched. No refund.', 'warn');
          break;
        case 'raceRecorded':
          this.toast(
            event.purseCents > 0
              ? `${ordinal(event.place)} place — ${formatMoney(event.purseCents)} purse!`
              : `${ordinal(event.place)} place. No purse.`,
            event.place === 1 ? 'good' : 'warn',
          );
          break;
        case 'leagueUnlocked':
          this.toast(`${LEAGUE_NAMES[event.league]} circuit unlocked!`, 'good');
          break;
        case 'facilityUpgraded':
          this.toast(`Facility upgraded to level ${event.level}.`, 'good');
          break;
        case 'rejected':
          this.toast(REJECT_MESSAGES[event.reason] ?? 'Not possible.', 'bad');
          break;
        default:
          break;
      }
    }
  }

  toast(text: string, tone: 'good' | 'warn' | 'bad'): void {
    const node = el('div', { class: `toast ${tone}` }, text);
    this.toasts.append(node);
    while (this.toasts.children.length > 4) this.toasts.firstElementChild?.remove();
    window.setTimeout(() => node.remove(), 3200);
  }

  set visible(value: boolean) {
    this.wrap.style.display = value ? '' : 'none';
  }

  dispose(): void {
    this.wrap.remove();
  }
}
